import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Resolvers } from '../../../types/Resolvers';
import { MarcaVehiculo, ModeloVehiculo } from '../../../types/vehicle.types';
import { getDataFromResolver } from '../../../services/resolvers';
import { SmartCapitalizePipe } from '../../../utils/pipes/smart-capitalize.pipe';
import { TopMarca } from '../../../types/product.types';
import { MarginWrapper } from '../../../layouts/margin-wrapper';
import {
  LucideAngularModule,
  ChevronDownIcon,
  ChevronUpIcon,
} from 'lucide-angular';
import { toSlug } from '../../../utils/slugs/slug.utils';

@Component({
  selector: 'allas-popular',
  imports: [
    CommonModule,
    SmartCapitalizePipe,
    MarginWrapper,
    LucideAngularModule,
  ],
  templateUrl: 'popular-sections.html',
})
export class PopularSectionsComponent {
  private route = inject(ActivatedRoute);

  // Referencias a los templates
  @ViewChild('makeItem', { static: true }) makeItemTemplate!: TemplateRef<any>;
  @ViewChild('modelItem', { static: true })
  modelItemTemplate!: TemplateRef<any>;
  @ViewChild('brandItem', { static: true })
  brandItemTemplate!: TemplateRef<any>;
  @ViewChild('adviceItem', { static: true })
  adviceItemTemplate!: TemplateRef<any>;

  // Propiedades para controlar la expansión de cada sección en mobile (acordeón)
  expandedSections: { [key: string]: boolean } = {
    makes: false,
    models: false,
    brands: false,
    advice: false,
  };

  // Propiedades para controlar la expansión en desktop (Ver Más/Ver Menos)
  expandedMakes = false;
  expandedModels = false;
  expandedBrands = false;
  expandedAdvice = false;

  // Iconos para el acordeón
  readonly chevronDownIcon = ChevronDownIcon;
  readonly chevronUpIcon = ChevronUpIcon;

  // Datos dinámicos desde la API
  popularBrands: Array<{ name: string; href: string; active: boolean }> = [];
  popularMakes: Array<{ name: string; href: string }> = [];
  popularModels: Array<{ name: string; href: string }> = [];

  constructor() {
    this.loadDataFromResolvers();
  }

  private loadDataFromResolvers(): void {
    const topBrands = getDataFromResolver<MarcaVehiculo[]>(
      Resolvers.ALL_VEHICLE_BRANDS,
      this.route
    );

    const topModels = getDataFromResolver<ModeloVehiculo[]>(
      Resolvers.ALL_VEHICLE_MODELS,
      this.route
    );

    const topBrandsProducts = getDataFromResolver<TopMarca[]>(
      Resolvers.ALL_PRODUCTS_TOP_BRANDS,
      this.route
    );

    this.popularMakes = this.processTopMakes(topBrands || []);
    this.popularModels = this.processTopModels(topModels || []);
    this.popularBrands = this.processTopBrands(topBrandsProducts || []);
  }

  private processTopBrands(
    brands: TopMarca[]
  ): Array<{ name: string; href: string; active: boolean }> {
    return brands.slice(0, 15).map((brand) => ({
      name: brand.marca,
      href: `/productos/marca/${toSlug(brand.marca)}`,
      active: true, // Por defecto, se puede cambiar según lógica de negocio
    }));
  }

  private processTopMakes(
    vehicles: MarcaVehiculo[]
  ): Array<{ name: string; href: string }> {
    // Agrupar por marca y obtener las más buscadas
    const brandMap = new Map<string, number>();

    vehicles.forEach((vehicle) => {
      const currentCount = brandMap.get(vehicle.marca) || 0;
      brandMap.set(vehicle.marca, currentCount + vehicle.busquedas);
    });

    // Convertir a array y ordenar por búsquedas
    return Array.from(brandMap.entries())
      .slice(0, 15) // Top 15 marcas
      .map(([marca, busquedas]) => ({
        name: `Repuestos para ${marca}`,
        href: `/repuestos/v/${marca.toLowerCase().replace(/[^a-z0-9]/g, '-')}`,
      }));
  }

  private processTopModels(
    vehicles: ModeloVehiculo[]
  ): Array<{ name: string; href: string }> {
    // Ordenar por búsquedas y tomar los top modelos
    return vehicles
      .sort((a, b) => b.busquedas - a.busquedas)
      .slice(0, 15) // Top 15 modelos
      .map((vehicle) => ({
        name: `Repuestos para ${vehicle.marca} ${vehicle.modelo}`,
        href: `/repuestos/v/${vehicle.marca.toLowerCase()}/${vehicle.modelo
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '-')}`,
      }));
  }

  adviceAndHowTos = [
    {
      name: '¿Qué Significa la Luz de la Batería?',
      href: '/bajo-construccion',
    },
    {
      name: '¿Cuánto Dura un Cambio de Aceite?',
      href: '/bajo-construccion',
    },
    {
      name: '¿Por Qué Se Enciende la Luz del Motor?',
      href: '/bajo-construccion',
    },
    { name: 'Cómo Cambiar los Frenos', href: '/bajo-construccion' },
    {
      name: 'Cómo Cambiar la Batería del Auto',
      href: '/bajo-construccion',
    },
    {
      name: 'Señales de Problemas en la Transmisión',
      href: '/bajo-construccion',
    },
    {
      name: 'Mantenimiento Preventivo del Auto',
      href: '/bajo-construccion',
    },
    {
      name: 'Cómo Revisar el Aceite del Motor',
      href: '/bajo-construccion',
    },
    {
      name: 'Cómo Cambiar el Filtro de Aire',
      href: '/bajo-construccion',
    },
  ];

  // Método para alternar la expansión de secciones en mobile
  onToggleSection(section: string) {
    this.expandedSections[section] = !this.expandedSections[section];
  }

  // Métodos para alternar la expansión en desktop
  toggleMakes() {
    this.expandedMakes = !this.expandedMakes;
  }

  toggleModels() {
    this.expandedModels = !this.expandedModels;
  }

  toggleBrands() {
    this.expandedBrands = !this.expandedBrands;
  }

  toggleAdvice() {
    this.expandedAdvice = !this.expandedAdvice;
  }

  // Método para trackBy en ngFor
  trackByItemName(index: number, item: any): string {
    return item.name;
  }
}
